import { Avatar, Card, Modal } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import OtpInputBox from "layouts/authentication/otp-sign-in/OtpInputBox";
import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import { useAuth } from "context";
import { verifyCustomerOtp } from "layouts/authentication/_request";
import { resendOtp } from "layouts/authentication/_request";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "450px",
  maxWidth: "500px",
  outline: "none !important",
  bgcolor: "#fff",
  boxShadow: 10,
  p: "24px",
  borderRadius: "12px",
  "@media (max-width:767px)": {
    width: "90%",
    padding: `18px`,
  },
};

const OtpConfirmationModal = ({
  identifier,
  type = "email",
  open = true,
  onClose,
  onVerify,
  onSendOTP,
  resendSlot,
}) => {
  const [otpValue, setOtpValue] = useState(["", "", "", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const inputRefs = useRef([]);
  const { showSnackbarWithMessage, setVerifiedCustomer } = useAuth();

  // Initialize refs array
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, 6);
  }, []);

  // Focus first input on open
  useEffect(() => {
    if (open && inputRefs.current[0]) {
      setTimeout(() => {
        inputRefs.current[0].focus();
      }, 100);
    }
  }, [open]);

  // Handle paste event for any input box
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").trim();

    // Check if pasted content is numeric and has the right length
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split("");
      setOtpValue(digits);

      // Focus on the last input after successful paste
      if (inputRefs.current[5]) {
        inputRefs.current[5].focus();
      }
    } else if (pastedData.length > 0) {
      // Show error message for invalid paste content
      setErrorMessage("Please paste a valid 6-digit code");
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otpValue[index] && index > 0) {
      const newOtp = [...otpValue];
      newOtp[index - 1] = "";
      setOtpValue(newOtp);
      inputRefs.current[index - 1].focus();
    }
  };

  const handleChange = (e, index) => {
    const value = e.target.value;

    if (isNaN(value) || value.length > 1) return;

    const newOtp = [...otpValue];
    newOtp[index] = value;
    setOtpValue(newOtp);

    if (index !== 5 && value) {
      inputRefs.current[index + 1].focus();
    }
  };

  const isOtpCompleted = () => {
    return otpValue.every((digit) => digit !== "");
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    // Reset OTP values when modal closes
    setOtpValue(["", "", "", "", "", ""]);
    setErrorMessage("");
  };

  const handleVerifyOtp = async () => {
    if (!isOtpCompleted()) {
      setErrorMessage("Please enter the complete 6-digit code");
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      const otpCode = otpValue.join("");

      if (onVerify) {
        // Use callback
        await onVerify(otpCode);
      } else {
        // Default implementation
        const payload =
          type === "email"
            ? { code: +otpCode, forgotPassword: false, userEmail: identifier }
            : { code: +otpCode, forgotPassword: false, userNumber: identifier };

        const {
          data: { success, message },
        } = await verifyCustomerOtp(payload);

        if (success) {
          if (type === "email") {
            setVerifiedCustomer((prev) => ({
              ...prev,
              verifiedAt: new Date().toISOString(),
            }));
          } else {
            setVerifiedCustomer((prev) => ({
              ...prev,
              phoneVerifiedAt: new Date().toISOString(),
            }));
          }

          showSnackbarWithMessage({
            type: "success",
            message,
          });

          handleClose();
        } else {
          setErrorMessage(message || "Verification failed");
          showSnackbarWithMessage({
            type: "error",
            message,
          });
        }
      }
    } catch (error) {
      setErrorMessage(error.message || "An error occurred");
      showSnackbarWithMessage({
        type: "error",
        message: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOtp = async () => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const {
        data: { success, message },
      } = await resendOtp(
        false,
        type === "phone" ? undefined : identifier,
        type === "phone" ? identifier : undefined
      );

      if (success) {
        // Reset OTP values
        setOtpValue(["", "", "", "", "", ""]);
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }

        showSnackbarWithMessage({
          type: "success",
          message,
        });
      } else {
        setErrorMessage(message || "Failed to resend OTP");
        showSnackbarWithMessage({
          type: "error",
          message,
        });
      }
    } catch (error) {
      setErrorMessage(error.message || "An error occurred");
      showSnackbarWithMessage({
        type: "error",
        message: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") return; // Prevent closing when clicking outside
        handleClose();
      }}
      aria-labelledby="otp-modal-title"
      aria-describedby="otp-modal-description"
    >
      <MDBox sx={style}>
        <MDBox textAlign="center">
          <MDBox mb="24px" mt="12px" display="flex" justifyContent="center" alignItems="center">
            <Avatar
              sx={{
                bgcolor: "#EBFFF8",
                width: 48,
                height: 48,
              }}
            >
              {type === "phone" ? (
                <PhoneIcon sx={{ fontSize: 32, color: "rgb(16, 121, 72)" }} />
              ) : (
                <MailOutlineIcon sx={{ fontSize: 32, color: "rgb(16, 121, 72)" }} />
              )}
            </Avatar>
          </MDBox>

          <MDTypography
            textAlign="center"
            sx={{ fontSize: "18px", color: "#181D27" }}
            fontWeight="medium"
          >
            {type === "phone"
              ? "Please Verify Your Phone Number"
              : type === "secondary"
              ? "Please Verify Your Email"
              : "Please check your email"}
          </MDTypography>
        </MDBox>

        {/* IDENTIFIER DETAILS */}
        <MDBox display="flex" flexDirection="column" alignItems="center">
          <p
            style={{
              fontSize: 16,
              color: "#475467",
              lineHeight: "20px",
              textAlign: "center",
            }}
          >
            We&apos;ve sent a 6 digit code to <span style={{ fontWeight: 600 }}>{identifier}</span>
          </p>

          <MDBox
            mt="30px"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="start"
          >
            <MDBox
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: { xs: 1, sm: 1.5 },
                width: "100%",
              }}
            >
              {otpValue.map((value, index) => (
                <React.Fragment key={index}>
                  <OtpInputBox
                    value={value}
                    onChange={(e) => handleChange(e, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onPaste={(e) => handlePaste(e)}
                    inputRef={(el) => (inputRefs.current[index] = el)}
                  />
                </React.Fragment>
              ))}
            </MDBox>

            {errorMessage && (
              <MDTypography color="error" sx={{ fontSize: "14px", mt: "8px" }} fontWeight="normal">
                {errorMessage}
              </MDTypography>
            )}

            {/* Resend OTP Code */}
            <MDBox
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              style={{ marginTop: "6px" }}
            >
              {resendSlot ? (
                resendSlot
              ) : (
                <MDTypography
                  color="textSecondary"
                  sx={{ fontSize: "14px", ml: "2px" }}
                  fontWeight="normal"
                >
                  Didn&apos;t get a code?
                  <span
                    style={{
                      textDecoration: "underline",
                      cursor: "pointer",
                      marginLeft: "3px",
                    }}
                    onClick={type === "secondary" ? onSendOTP : isLoading ? undefined : handleSendOtp}
                  >
                    Click to resend.
                  </span>
                </MDTypography>
              )}
            </MDBox>
          </MDBox>
        </MDBox>

        {/* BACK AND NEXT BTN */}
        <MDBox display="flex" gap={2} alignItems="center" style={{ marginTop: "30px" }}>
          {onClose && (
            <MDButton
              fullWidth
              onClick={handleClose}
              variant="outlined"
              color="black"
              sx={{
                borderRadius: "8px",
                padding: "10px",
                border: "1px solid #D0D5DD",
                textDecoration: "lowercase",
                boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 500,
              }}
              disabled={isLoading}
            >
              Cancel
            </MDButton>
          )}

          <MDButton
            fullWidth
            variant="contained"
            color="success"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontSize: "16px",
              fontWeight: 500,
            }}
            disabled={isLoading || !isOtpCompleted()}
            onClick={handleVerifyOtp}
          >
            Verify
          </MDButton>
        </MDBox>
      </MDBox>
    </Modal>
  );
};

OtpConfirmationModal.propTypes = {
  identifier: PropTypes.string.isRequired,
  type: PropTypes.oneOf(["email", "phone", "secondary"]),
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onVerify: PropTypes.func,
  onSendOTP: PropTypes.func,
  resendSlot: PropTypes.node,
};

export default OtpConfirmationModal;
